import React, { FC } from 'react';
import { connectDeep } from '../../redux-utils';
import styled from 'styled-components';

import App from '../../models/app';
import i18n from 'i18n';
import { viewAllLimit, hidenCollectionsIds } from '../../config';
import { AppType, CollectionType, roundEdgeDevices } from '../../constants';
import AppShapedImage from './AppShapedImage/index';
import ItemGrid from './AppContentGrid/ItemGrid';
import Typography from './Typography';
import { ButtonLink } from './Button/index';
import { ContentList, ItemRow } from './AppContentList/index';

const Container = styled.div`
  background: var(--sys-color--surface);
`;

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

interface SmallAppListProps {
  apps: App[];
  collectionId?: string;
  deviceName?: string;
  onClick?: (appId: string) => void;
}

export const SmallAppList: FC<SmallAppListProps> = ({ apps, collectionId, deviceName = '', onClick }) => (
  <ContentList style={{ padding: '0 16px 16px 16px' }}>
    {apps.map((app) => {
      const queryParam = collectionId ? `?collectionId=${collectionId}` : '';
      const linkTo = `/v2/tile/${app.id}${queryParam}`;

      const photo = (
        <AppShapedImage
          photoProperties={{
            src: app.banners?.[0]?.uri || '',
            alt: app.name,
            width: app.banners?.[0]?.width,
            height: app.banners?.[0]?.height,
            isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
          }}
          width={90}
          height={90}
          borderRadius="16px"
          borderReservedSpace={false}
        />
      );

      return (
        <ItemRow
          key={app.id}
          id={app.id}
          title={app.name}
          linkTo={linkTo}
          photo={photo}
          subtitle={app.isInstalledOrInstalling ? 'installed' : undefined}
          onClick={() => {
            if (onClick) {
              onClick(app.id);
            }
          }}
        />
      );
    })}
  </ContentList>
);

const SmallCardContainer = styled.div`
  width: 100%;
`;

const SmallItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 0px 16px;
`;

const ClockContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AppIcon = styled.img`
  width: 100%;
  max-width: 110px;
  height: auto;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  object-fit: contain;
  margin-bottom: 12px;
`;

interface SmallGridProps {
  apps: App[];
  collectionId?: string;
  deviceName: string;
  isClock?: boolean;
  onClick?: (appId: string) => void;
}

export const SmallGrid: FC<SmallGridProps> = ({ apps, collectionId, deviceName, isClock, onClick }) => (
  <ClockContentContainer>
    <SmallItemsGrid>
      {apps.map((app) => {
        const queryParam = collectionId ? `?collectionId=${collectionId}` : '';
        const linkTo = isClock ? `/v2/clock/${app.id}${queryParam}` : `/v2/app/${app.id}${queryParam}`;

        const photo = isClock ? (
          <AppShapedImage
            photoProperties={{
              src: app.banners?.[0]?.uri || '',
              alt: app.name,
              width: app.banners?.[0]?.width,
              height: app.banners?.[0]?.height,
              isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
            }}
            width={110}
            height={110}
            borderRadius="16px"
            borderReservedSpace={true}
          />
        ) : (
          <AppIcon src={app.icon?.uri || ''} alt={app.name} />
        );

        return (
          <SmallCardContainer key={app.id}>
            <ItemGrid
              id={app.id}
              title={app.name}
              subtitle={app.isInstalledOrInstalling ? 'installed' : undefined}
              linkTo={linkTo}
              onClick={() => {
                if (onClick) {
                  onClick(app.id);
                }
              }}
              photo={photo}
            />
          </SmallCardContainer>
        );
      })}
    </SmallItemsGrid>
  </ClockContentContainer>
);

type ReduxProps = ReturnType<typeof mapStateToProps>;

interface CollectionListProps extends ReduxProps {
  name: string;
  apps: App[];
  isClock?: boolean;
  path: string;
  id: string;
  collectionType?: string;
  inputText?: string;
  shouldDisplayViewAll?: boolean;
  footer?: React.ReactNode;
  appType?: AppType;
  onClickViewAll?: () => void;
}

export const ItemsPreviewComponent: FC<CollectionListProps> = ({
  name,
  isClock,
  path,
  apps,
  id,
  shouldDisplayViewAll,
  footer,
  collectionType,
  inputText,
  gregEnabled,
  optimizelyUser,
  onClickViewAll,
  deviceName,
  appType,
}) => {
  const appList = apps.slice(0, 3);
  const displayViewAll = shouldDisplayViewAll ?
    shouldDisplayViewAll : apps.length > viewAllLimit;

  const payload = inputText ? { inputText } : { collectionId: id, collectionType };
  let collectionId: string | undefined;
  if (collectionType && collectionType.toLowerCase() === CollectionType.COLLECTION.toLowerCase()) {
    collectionId = id;
  }

  const showList = appType && appType.toLowerCase() === AppType.TILE;

  const handleOnClickViewAll = () => {
    if (
      hidenCollectionsIds.includes(collectionId || '') &&
      gregEnabled &&
      apps &&
      apps.length > 0 &&
      optimizelyUser
    ) {
      optimizelyUser.trackEvent('greg_collection_open');
    }

    if (onClickViewAll) {
      onClickViewAll();
    }
    return true;
  }

  return (
    <Container>
      <TopContainer>
        <Typography variant="titleMediumEmphasized" color="var(--sys-color--on-surface)">
          {name}
        </Typography>
        {displayViewAll && (
          <ButtonLink
            to={path}
            pageDirection="FORWARD"
            id={id}
            ariaLabel={id}
            variant="text"
            compact={true}
            onClick={handleOnClickViewAll}
          >
            {i18n('component.Category.viewAllText')()}
          </ButtonLink>
        )}
      </TopContainer>
      {showList ? (
        <SmallAppList apps={appList} collectionId={collectionId} deviceName={deviceName} />
      ) : (
        <SmallGrid
          apps={appList}
          collectionId={collectionId}
          deviceName={deviceName}
          isClock={isClock}
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const deviceName = state.registry.currentUser?.device?.productName || null;
  const galleryState = state.gallery;

  const { gregEnabled, userContext } = galleryState.experiments;

  return {
    deviceName,
    gregEnabled,
    optimizelyUser: userContext
  }
};

export const ItemsPreview = connectDeep(mapStateToProps)(ItemsPreviewComponent);
